/**
 * "A form fill is in progress" flag, shared across app bundles.
 *
 * WHY THIS EXISTS
 *
 * The technician PWA registers its service worker with `registerType: 'autoUpdate'`
 * (skipWaiting + clientsClaim), and `SWUpdatePrompt` asks the browser to check for
 * a new one on every `visibilitychange`, every window `focus`, and every `online`
 * event. When a new build is found the worker self-activates and the page RELOADS,
 * with no prompt and nothing the page can veto.
 *
 * Those three triggers are precisely the moments a technician mid-report comes
 * back to the app: returning from the camera, from a phone call, from a locked
 * screen, or walking out of a dead zone. So the app can reload out from under a
 * half-filled Field Service Report at exactly the wrong moment. What survives is
 * whatever the local autosave holds — which strips photo/signature bytes to
 * metadata husks, and husks BLOCK submit, so the tech can end up unable to file
 * the report at all. The tech-PWA vite config already names this: "auto-reload
 * destroying an in-progress incident report's unsaved photos".
 *
 * The real fix is migrating that registration to 'prompt' mode, which needs a
 * bridge release (an installed client has `auto = true` baked in permanently and
 * cannot show a banner). Until then, this suppresses the update CHECK while a
 * fill is open — which removes the three triggers above, i.e. the ones that
 * actually fire in the field. It is a narrowing, not a guarantee: a real
 * navigation can still discover a new worker.
 *
 * Deliberately reference-counted rather than boolean: a fill surface can mount
 * twice across a route transition, and a naive `false` on the first unmount would
 * unguard a fill that is still open.
 */

/**
 * Upper bound on how long an open fill may HOLD app updates (#4741): the
 * update UI stays hidden and automatic update checks stay skipped. A fill
 * surface that never unmounts (a tab parked on a form overnight) must not pin
 * an unsupported build forever; past this the update surfaces normally.
 *
 * PENDING PRODUCT CONFIRMATION (#4741): 30 minutes is an engineering default,
 * not a ruling. Whether an FSR fill may outlast it (and then be interrupted by
 * the forced-update modal) is a product call — confirm before treating it as
 * final.
 */
export const FORM_FILL_MAX_HOLD_MS = 30 * 60 * 1000;

let openFills = 0;
/** When the current continuous run of open fills began (null when none open). */
let heldSince: number | null = null;
let capTimer: ReturnType<typeof setTimeout> | null = null;
const listeners = new Set<() => void>();

function notify(): void {
  listeners.forEach((listener) => {
    try {
      listener();
    } catch {
      /* a subscriber must never break the guard */
    }
  });
}

/** Call when a form fill surface mounts. Returns the matching release function. */
export function beginFormFill(): () => void {
  if (openFills === 0) {
    // Per-form, not per-session: the clock restarts whenever the last fill
    // closes and a new one opens (product can tighten this to per-session).
    heldSince = Date.now();
    // Wake subscribers when the hold expires, so the update UI re-renders and
    // the check resumes without waiting for an unrelated event.
    capTimer = setTimeout(notify, FORM_FILL_MAX_HOLD_MS);
  }
  openFills += 1;
  notify();
  let released = false;
  return () => {
    if (released) return;
    released = true;
    openFills = Math.max(0, openFills - 1);
    if (openFills === 0) {
      heldSince = null;
      if (capTimer !== null) clearTimeout(capTimer);
      capTimer = null;
      notify();
    }
  };
}

/** True while at least one form fill surface is mounted. */
export function isFormFillInProgress(): boolean {
  return openFills > 0;
}

/**
 * True while app updates should be held back for an open fill: a fill is open
 * AND it has been open for less than FORM_FILL_MAX_HOLD_MS. Consulted by the
 * update UI (never render the forced modal over a form) and by every automatic
 * update check (never trigger a service-worker takeover under a form).
 */
export function shouldHoldUpdatesForFormFill(): boolean {
  return (
    openFills > 0 &&
    heldSince !== null &&
    Date.now() - heldSince < FORM_FILL_MAX_HOLD_MS
  );
}

/**
 * Subscribe to hold changes (a fill opens, the last fill closes, or the hold
 * cap expires). Shaped for React's useSyncExternalStore. Returns unsubscribe.
 */
export function subscribeFormFill(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}
